import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { AppContext } from "AppContext";
import { showToast, validateEmail } from "utils/utility";
import { useUserForgotPasswordData } from "libs/data-access";
import { ALL_ALLOWED_ROUTES } from "App";

interface ForgotPasswordParams {
    email: string;
    otp: string;
    password: string;
    confirmPassword: string;
}

export default function useForgotPassword() {

    const navigate = useNavigate();

    const {
        setRole,
        setRefreshToken,
        setToken,
        setIsLoggedIn,
        handleUser,
        setUserAuthorities,
        setActiveRoute
    } = useContext(AppContext);

    const {
        postUserForgotPasswordData
    } = useUserForgotPasswordData();

    const [activeStep, setActiveStep] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isResending, setIsResending] = useState<boolean>(false);
    const [resendTimer, setResendTimer] = useState<number>(60);
    const [canResend, setCanResend] = useState<boolean>(true);
    const [params, setParams] = useState<ForgotPasswordParams>({
        email: "",
        otp: "",
        password: "",
        confirmPassword: "",
    });


    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (!canResend && resendTimer > 0) {
            timer = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        } else if (resendTimer === 0) {
            setCanResend(true);
            setResendTimer(60);
        };

        return () => clearInterval(timer);
    }, [canResend, resendTimer]);


    // HANDLER FUNCTION
    const handleParamsChange = (key: keyof ForgotPasswordParams, value: string) => {
        setParams((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleCancel = () => {
        navigate("/login");
    };


    // API CALLS
    const requestOTP = async () => {
        if (!validateEmail(params.email)) {
            showToast({
                type: "warning",
                message: "Please enter a valid email id!"
            });
            return;
        };

        setIsLoading(true);

        const response = await postUserForgotPasswordData(`/request-otp?userName=${params.email}`, null);

        setIsLoading(false);

        if (response.isSuccess) {
            setActiveStep(2);
            setCanResend(false);
        } else {
            if (response.status === 429) {
                resendOTP(true);
                return;
            };

            console.log("Error while requesting for otp: ", response);
            showToast({
                type: "error",
                message: response.message
            });
        };
    };

    const resendOTP = async (initiallyResend = false) => {
        if (!canResend) return;

        if (!validateEmail(params.email)) {
            showToast({
                type: "warning",
                message: "Please enter a valid email id!"
            });
            return;
        };

        setIsResending(true);
        setCanResend(false);

        const response = await postUserForgotPasswordData(`/resend-otp?userName=${params.email}`, null);

        setIsResending(false);

        if (response.isSuccess) {
            if (initiallyResend) setActiveStep(2);

            showToast({
                type: "success",
                message: "OTP has been resent successfully"
            });
        } else {
            console.log("Error while resending otp: ", response);
            showToast({
                type: "error",
                message: response.message
            });
            setCanResend(true);
        };
    };

    const verifyOTP = async () => {
        if (!validateEmail(params.email)) {
            showToast({
                type: "warning",
                message: "Please enter a valid email id!"
            });
            return;
        };

        if (!params.otp) {
            showToast({
                type: "warning",
                message: "Please enter a valid OTP!"
            });
            return;
        };

        if (!params.password) {
            showToast({
                type: "warning",
                message: "Please enter a password!"
            });
            return;
        };

        if (!params.confirmPassword) {
            showToast({
                type: "warning",
                message: "Please enter confirm password!"
            });
            return;
        };

        if (params.password !== params.confirmPassword) {
            showToast({
                type: "warning",
                message: "Confirm password and password does not match!"
            });
            return;
        };

        setIsLoading(true);

        const response = await postUserForgotPasswordData("/verify-otp", {
            userName: params.email,
            otp: params.otp,
            password: params.password,
            confirmPassword: params.confirmPassword,
        });

        setIsLoading(false);

        if (response.isSuccess) {
            console.log("OTP verified successfully: ", response);
            showToast({
                type: "success",
                message: "Password reset successful"
            });

            const resData = response.data?.data;
            const user = response.data?.data.user;
            const othersDetails = response.data?.extraDetails;
            const userAuthorities = response.data?.extraDetails?.authorities;

            handleUser(user);
            setUserAuthorities(userAuthorities);
            setRole(othersDetails.role);
            setToken(resData?.accessToken);
            setRefreshToken(resData?.refreshToken);
            setIsLoggedIn(true);
            setActiveRoute({ route: ALL_ALLOWED_ROUTES[0], SubMenuIndex: [] });

            localStorage.setItem("userAuthorities", JSON.stringify(userAuthorities));
            localStorage.setItem("role", JSON.stringify(othersDetails.role));
            localStorage.setItem("user", JSON.stringify(user));
            localStorage.setItem("refreshToken", resData?.refreshToken);
            localStorage.setItem("token", resData?.accessToken);
            localStorage.setItem("isLoggedIn", JSON.stringify(true));
            localStorage.setItem("activeRoute", JSON.stringify({ route: ALL_ALLOWED_ROUTES[0], SubMenuIndex: [] }));
            // filterRoutesByRoles(ALL_ROUTES)
            window.location.reload();
        } else {
            console.log("Error while requesting for otp: ", response);
            showToast({
                type: "error",
                message: response.message
            });
        };
    };


    return {
        activeStep,
        setActiveStep,
        params,
        handleParamsChange,
        requestOTP,
        resendOTP,
        verifyOTP,
        handleCancel,
        isLoading,
        isResending,
        canResend,
        resendTimer,
    };
} 